import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Type, Bold, Italic, Underline, RotateCcw } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { TEXT_AD_FONT_OPTIONS } from '@/constants/textAdFonts';
import type { RichTextStyle } from '@/types/ads';

export default function TextBlockStyles({ value, defaults, onChange, index }: {
  value?: RichTextStyle; defaults: RichTextStyle; onChange: (style?: RichTextStyle) => void; index: number;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !triggerRef.current?.contains(event.target) && !panelRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', closeOutside, true);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside, true);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    // Keep the opening anchor while edits resize and move the text block.
    const anchor = trigger.getBoundingClientRect();
    const position = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const { width, height } = panel.getBoundingClientRect();
      const left = Math.max(16, Math.min(anchor.left, window.innerWidth - width - 16));
      const top = anchor.bottom + height + 24 <= window.innerHeight
        ? anchor.bottom + 8 : Math.max(16, anchor.top - height - 8);
      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.visibility = 'visible';
    };
    position();
    const observer = new ResizeObserver(position);
    if (panelRef.current) observer.observe(panelRef.current);
    window.addEventListener('resize', position);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', position);
    };
  }, [open]);
  const { t } = useTranslation();
  const l = t.textBlockStyle;
  const set = (patch: Partial<RichTextStyle>) => onChange({ ...value, ...patch });
  return <>
    <button ref={triggerRef} type="button" aria-label={`${l.title} ${index + 1}`} aria-expanded={open}
      onClick={() => setOpen(current => !current)}
      className="flex shrink-0 cursor-pointer rounded p-1 text-gray-500 hover:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-500">
      <Type className="h-4 w-4" />
    </button>
    {open && createPortal(<div ref={panelRef} role="group" aria-label={l.title}
      style={{ visibility: 'hidden' }}
      className="fixed z-[1000] flex w-max max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] flex-wrap items-center gap-1 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 text-sm text-gray-800 shadow-xl">
      <select aria-label={l.font} title={l.font}
        className="h-8 w-36 cursor-pointer rounded border border-gray-300 px-1"
        value={value?.fontFamily ?? defaults.fontFamily ?? 'roboto_regular'}
        onChange={event => set({ fontFamily: event.target.value || undefined })}>
        {TEXT_AD_FONT_OPTIONS.map(font => <option key={font.value} value={font.value}>{font.label}</option>)}
      </select>
      <input aria-label={l.size} title={l.size} type="number" min={10} max={200}
        className="h-8 w-16 rounded border border-gray-300 px-1" value={value?.fontSize ?? defaults.fontSize ?? 24}
        onBlur={event => { if (event.target.value) set({ fontSize: Math.max(10, Math.min(200, Number(event.target.value))) }); }}
        onChange={event => set({ fontSize: event.target.value ? Number(event.target.value) : undefined })} />
      <label title={l.color} className="relative flex h-8 w-8 cursor-pointer flex-col items-center justify-center rounded hover:bg-indigo-50">
        <Type aria-hidden="true" className="h-4 w-4" />
        <span className="h-1 w-5 rounded-sm border border-gray-200" style={{ backgroundColor: value?.color ?? defaults.color ?? '#ffffff' }} />
        <input aria-label={l.color} type="color" className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          value={value?.color ?? defaults.color ?? '#ffffff'} onChange={event => set({ color: event.target.value })} />
      </label>
      <span aria-hidden="true" className="mx-1 h-5 border-l border-gray-200" />
      {([{ key: 'bold', Icon: Bold }, { key: 'italic', Icon: Italic }, { key: 'underline', Icon: Underline }] as const).map(({ key, Icon }) =>
        <button type="button" key={key} aria-label={l[key]} title={l[key]}
          aria-pressed={!!value?.[key]}
          className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded hover:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-500 ${value?.[key] ? 'bg-indigo-100 text-indigo-700' : ''}`}
          onClick={() => set({ [key]: !value?.[key] })}><Icon aria-hidden="true" className="h-4 w-4" /></button>)}
      <button type="button" aria-label={l.reset} title={l.reset}
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-gray-500 hover:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-500"
        onClick={() => onChange(undefined)}><RotateCcw aria-hidden="true" className="h-4 w-4" /></button>
    </div>, document.body)}
  </>;
}
